import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchSocialPosts, fetchSocialAccounts } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageSquare, Users, Plus, ArrowRight } from "lucide-react";
import { PostStatus, Platform } from "@/types";
import { PLATFORM_MAP } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { PageShell } from "@/components/page-shell";

export const Route = createFileRoute("/")({
  component: DashboardPage,
});

function DashboardPage() {
  const { data: postsData } = useQuery({
    queryKey: ["social-posts", PostStatus.Processed, undefined, undefined, 0],
    queryFn: () => fetchSocialPosts({ status: PostStatus.Processed, limit: 1, offset: 0 }),
  });

  const { data: accounts } = useQuery({
    queryKey: ["social-accounts"],
    queryFn: () => fetchSocialAccounts(),
  });

  const totalPosts = postsData?.count ?? 0;
  const totalAccounts = accounts?.length ?? 0;
  const activeAccounts = accounts?.filter((a) => a.status === "connected").length ?? 0;

  const platformCounts: Record<string, number> = {};
  for (const acc of accounts ?? []) {
    platformCounts[acc.platform] = (platformCounts[acc.platform] ?? 0) + 1;
  }

  return (
    <PageShell title="Dashboard" maxWidth="3xl">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl border bg-card p-4">
          <div className="text-2xl font-bold">{totalPosts}</div>
          <div className="text-xs text-muted-foreground">Posts</div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="text-2xl font-bold">{totalAccounts}</div>
          <div className="text-xs text-muted-foreground">Accounts</div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="text-2xl font-bold">{activeAccounts}</div>
          <div className="text-xs text-muted-foreground">Active</div>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="text-2xl font-bold">{Object.keys(platformCounts).length}</div>
          <div className="text-xs text-muted-foreground">Platforms</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link to="/posts">
          <Button className="w-full h-auto py-4 gap-3 justify-start" variant="default">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <Plus className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium">New Post</div>
              <div className="text-xs opacity-80">Create & schedule</div>
            </div>
          </Button>
        </Link>
        <Link to="/feed">
          <Button className="w-full h-auto py-4 gap-3 justify-start" variant="outline">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium">Feed</div>
              <div className="text-xs text-muted-foreground">Browse posts</div>
            </div>
          </Button>
        </Link>
        <Link to="/accounts">
          <Button className="w-full h-auto py-4 gap-3 justify-start" variant="outline">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium">Accounts</div>
              <div className="text-xs text-muted-foreground">Manage connections</div>
            </div>
          </Button>
        </Link>
      </div>

      {/* Connected Platforms */}
      <section>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
          Connected Platforms
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(platformCounts).map(([platform, count]) => {
            const config = PLATFORM_MAP[platform as Platform];
            const Icon = config?.icon ?? MessageSquare;
            return (
              <div
                key={platform}
                className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2.5"
              >
                <Icon className={cn("w-4 h-4", config?.color ?? "text-slate-500")} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium capitalize truncate">{platform}</div>
                  <div className="text-xs text-muted-foreground">
                    {count} account{count !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Recent Feed Preview */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Recent Posts
          </h2>
          <Link to="/feed">
            <Button variant="ghost" size="sm" className="gap-1 text-xs">
              View all <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>
        </div>
        <div className="rounded-xl border bg-card overflow-hidden">
          {postsData?.transformedData?.slice(0, 3).map((post) => (
            <Link key={post.id} to="/feed" search={{ post: post.id }}>
              <div className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-muted/40 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  {post.media[0]?.url ? (
                    <img
                      src={post.media[0].url}
                      alt=""
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{post.caption || "(no caption)"}</div>
                  <div className="text-xs text-muted-foreground capitalize">
                    {post.platform} &middot; {post.status}
                  </div>
                </div>
              </div>
            </Link>
          )) ?? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No posts yet.{" "}
              <Link to="/posts" className="text-primary underline">
                Create one
              </Link>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
